// FourDay view for the upcoming page, modeled after Week but with 4 days
import FullCalendar from '@fullcalendar/react';
import { startOfDay, startOfWeek } from 'date-fns';
import { useExternalCalendarFullCalendarEvents } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTasks, useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FullCalendarWeek } from 'shared/components/ui/full-calendar-week';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { PageEvents } from 'shared/services/tracking';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useGoalDeadlineEvents } from '../../hooks/use-goal-deadline-events';
import { useTaskEvents } from '../../hooks/use-task-events';

const dayRange = 4;

export type FourDayProps = {
  rangeReferenceDate?: Timestamp;
  scrollTime?: string;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
};

export const FourDay: React.FC<FourDayProps> = ({
  rangeReferenceDate,
  scrollTime,
  fullCalendarRef,
}) => {
  useTrackPageVisitEvent(PageEvents.UpcomingFourDays, undefined); // You may want a new event type
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const tasks = useTasks();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const startDate = useMemo(
    () =>
      startOfDay(
        rangeReferenceDate ??
          startOfWeek(today, {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
      ),
    [rangeReferenceDate, today, weekStartsOn],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          !task.deletedAt &&
          (task.type !== TaskType.Habit ||
            !user?.settings?.upcoming?.hideHabits),
      ),
    [tasks, user?.settings?.upcoming?.hideHabits],
  );

  const navigate = useNavigate();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const taskEvents = useTaskEvents(filteredTasks, {
    rangeReferenceDate: startDate,
    dayRange,
    hideCompleted: user?.settings?.upcoming?.hideCompleted,
  });
  const goalDeadlineEvents = useGoalDeadlineEvents();
  const externalCalendarEvents = useExternalCalendarFullCalendarEvents(
    startDate,
    dayRange,
  );
  const { onEventChange, onDrop, onSelect, onDateTime } =
    useCalendarEventControls(lifeAreas, goals, filteredTasks);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
        return;
      }

      const lifeArea = lifeAreas.find(({ id: goalId }) => goalId === id);
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [filteredTasks, goals, lifeAreas, openTaskDetail, navigate],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }

      const daySchedule = getScheduleByDate(task.schedules, { date, today });

      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [onComplete, filteredTasks, today],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.upcoming?.hideExternalCalendarEvents
        ? []
        : externalCalendarEvents),
      ...taskEvents,
      ...goalDeadlineEvents,
    ],
    [
      externalCalendarEvents,
      goalDeadlineEvents,
      taskEvents,
      user?.settings?.upcoming?.hideExternalCalendarEvents,
    ],
  );

  useEffect(() => {
    const nowIndicator = scrollContainerRef.current?.querySelector(
      '.fc-timegrid-now-indicator-line',
    );
    if (nowIndicator && nowIndicator instanceof HTMLElement) {
      const rect = nowIndicator.getBoundingClientRect();
      const containerRect = scrollContainerRef.current!.getBoundingClientRect();
      const relativeLeft =
        rect.left - containerRect.left + scrollContainerRef.current!.scrollLeft;
      const relativeTop =
        rect.top - containerRect.top + scrollContainerRef.current!.scrollTop;
      scrollContainerRef.current?.scrollTo({
        left: relativeLeft - 62,
        top: relativeTop - containerRect.height / 3,
      });
    }
  }, [startDate]);

  return (
    <FullCalendarWeek
      ref={scrollContainerRef}
      fullCalendarRef={fullCalendarRef}
      tasks={filteredTasks}
      goals={goals}
      lifeAreas={lifeAreas}
      onCompleteTask={_onComplete}
      onClickEvent={_openEventDetail}
      events={events}
      rangeReferenceDate={startDate}
      scrollTime={scrollTime}
      rangeDays={dayRange}
      eventChange={onEventChange}
      drop={onDrop}
      dateClick={onDateTime}
      select={onSelect}
      selectConstraint={{
        startTime: '00:00',
        endTime: '23:59:59',
      }}
      timeFormat={getTimeFormat(user)}
      selectable
      editable
      droppable
    />
  );
};
